import { useState, useEffect } from "react";
import {
    DATE_POSTED_FILTERS,
    QUALIFICATION_FILTERS,
} from "../constants/constants";
import {
    AllJobs,
    JobFilter,
    MobileFilters,
    PaginationComponent,
} from "../../shared-components";
import { IFiltersGroup } from "../../shared-components/interfaces/current-affairs";
import { getFreshJobsCategories, getTopJobLocations } from "../../network/APIs/categories";
import { IJobCategories } from "../../shared-components/interfaces/job-categories.interface";
import { IHandlePageChangeEvent } from "../../shared-components/interfaces/common.interface";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { GovJobs } from "../../shared-components";
import { deepCopyJSON } from "../../shared-components/constants/constants";
import { usePrivateJobsList } from "../../network/APIs/jobs";
import { Briefcase, ChevronRight, X, Search } from "lucide-react";

function FreshersJob() {
    const ITEMS_PER_PAGE = 10;
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const categoryId = searchParams.get("categoryId");
    const search = searchParams.get("search");
    const isLocation = searchParams.get("type") === "location";
    const locationId = searchParams.get("id");
    const page = searchParams.get("page");
    const date_posted = searchParams.get("date_posted");
    const qualification = searchParams.get("qualification");
    const location = searchParams.get("location");

    const [isFiltersLoaded, setFiltersLoaded] = useState(false);

    const [query, setQuery] = useState<any>();
    const { data: filteredItems } = usePrivateJobsList(query);
    const [filters, setFilters] = useState<IFiltersGroup[]>([
        deepCopyJSON(QUALIFICATION_FILTERS),
        // deepCopyJSON(DATE_POSTED_FILTERS),
    ]);

    useEffect(() => {
        const fetchPromises: any[] = [getFreshJobsCategories()];
        if (isLocation) {
            fetchPromises.push(getTopJobLocations());
        }

        Promise.all(fetchPromises)
            .then(([catResult, locResult]) => {
                let updatedFilters = [...filters];

                if (catResult.success) {
                    const newCategory: IFiltersGroup = {
                        label: "Category",
                        type: "checkbox",
                        value: "categoryId",
                        filters: catResult.data?.categories.map((category: IJobCategories) => ({
                            id: Number(category.id),
                            lable: category.category_name,
                            value: category.id,
                        })) || [],
                    };
                    updatedFilters = [newCategory, ...updatedFilters];
                }

                if (isLocation && locResult && locResult.success) {
                    const newLocation: IFiltersGroup = {
                        label: "Location",
                        type: "checkbox",
                        value: "location",
                        filters: locResult.data.map((loc: any) => ({
                            id: Number(loc.location_id),
                            lable: loc.city,
                            value: loc.location_id,
                            isChecked: Number(loc.location_id) === Number(locationId)
                        })),
                    };
                    // Insert Location after Category or at start
                    updatedFilters = [updatedFilters[0], newLocation, ...updatedFilters.slice(1)];
                }

                // SYNC WITH URL PARAMS IMMEDIATELY
                const params: any = { categoryId, location };
                const syncedFilters = updatedFilters.map((filterGroup) => {
                    const val = params[filterGroup.value];
                    if (val) {
                        const selected = (val + "").split(",");
                        return {
                            ...filterGroup,
                            filters: filterGroup.filters.map((f) => ({
                                ...f,
                                isChecked: selected.includes(f.value + ""),
                            })),
                        };
                    } else if (filterGroup.value === "location" && isLocation && locationId) {
                        return {
                            ...filterGroup,
                            filters: filterGroup.filters.map((f) => ({
                                ...f,
                                isChecked: Number(f.value) === Number(locationId),
                            })),
                        };
                    }
                    return filterGroup;
                });

                setFilters(syncedFilters);
                setFiltersLoaded(true);
            })
            .catch(() => {
                setFiltersLoaded(true);
            });
    }, [isLocation, locationId]);


    // On category query param change
    useEffect(() => {
        const newQuery = {
            categoryId: Array.isArray(categoryId) ? categoryId.join(",") : categoryId,
            location: Array.isArray(location) ? location.join(",") : location,
            search,
            date_posted,
            qualification,
            offset:
                ((isNaN(Number(page)) || !Number(page) ? 1 : Number(page)) - 1) *
                ITEMS_PER_PAGE,
            limit: ITEMS_PER_PAGE,
        };
        setQuery(newQuery);
    }, [categoryId, location, search, page, date_posted, qualification]);

    // Handle filter
    const handleFilterChange = (appliedFilters: IFiltersGroup[], options?: { replace?: boolean }) => {
        if (!isFiltersLoaded) return;

        const queryParams: { [key: string]: any } = {};

        appliedFilters.forEach((filter_group) => {
            const groupValues: any = [];

            filter_group.filters.forEach((filter) => {
                if (filter.isChecked) {
                    groupValues.push(filter.value);
                }
            });

            if (groupValues?.length) {
                queryParams[filter_group.value] =
                    groupValues.length === 1 ? groupValues[0] : groupValues;
            }
        });

        setFilters(appliedFilters);

        searchParams.delete("categoryId");
        searchParams.delete("location");
        searchParams.delete(DATE_POSTED_FILTERS.value);
        searchParams.delete(QUALIFICATION_FILTERS.value);
        Object.keys(queryParams).forEach((key) => {
            searchParams.append(key, queryParams[key]);
        });
        searchParams.delete("page");
        setSearchParams(searchParams, { replace: options?.replace });
    };

    const handleSearch = (search: string) => {
        searchParams.delete("page");
        if (search && search.length) {
            searchParams.set("search", search);
            setSearchParams(searchParams);
        } else {
            searchParams.delete("search");
            setSearchParams(searchParams);
        }
    };

    const handlePageChange = ({ page }: IHandlePageChangeEvent) => {
        searchParams.set("page", page + "");
        setSearchParams(searchParams);
    };

    const clearFilters = () => {
        setSearchParams({});
        setFilters((prev) =>
            prev.map((group) => ({
                ...group,
                filters: group.filters.map((f) => ({ ...f, isChecked: false })),
            })),
        );
    };

    const selectedFilters = filters.flatMap((group) =>
        group.filters
            .filter((f) => f.isChecked)
            .map((f) => ({
                label: f.lable,
                value: f.value,
                groupLabel: group.label,
                groupValue: group.value,
            })),
    );

    const hasActiveFilters = selectedFilters.length > 0;

    return (
        <>
            <Helmet>
                <title>
                    Apply for 1000+ Job Vacancies on Freshment, Search Fresher Jobs,
                    Engineering, MBA Job Openings
                </title>

                <meta
                    name="title"
                    content="Apply for 1000+ Job Vacancies on Freshment, Search Fresher Jobs, Engineering, MBA Job Openings"
                />

                <meta
                    name="description"
                    content="Apply for 1000 Software, Engineering, MBA Sales, Finance, Marketing, Diploma, Commerce, Arts  Job Openings on Freshment. Get Daily Job Alerts. ✓ Work from Home ✓ Online ✓ Freshers"
                />

                <meta
                    name="image"
                    content="https://freshmentapp.com/static/media/freshmentLogo.a5200e9f.svg"
                />

                <link rel="canonical" href="https://freshmentapp.com/freshers-job" />
                <link
                    rel="alternate"
                    href="https://freshmentapp.com/freshers-job"
                    hrefLang="en-in"
                />

                <meta
                    itemProp="description"
                    content="Apply for 1000 Software, Engineering, MBA Sales, Finance, Marketing, Diploma, Commerce, Arts  Job Openings on Freshment. Get Daily Job Alerts. ✓ Work from Home ✓ Online ✓ Freshers"
                />

                <meta
                    property="og:title"
                    content="Apply for 1000+ Job Vacancies on Freshment, Search Fresher Jobs, Engineering, MBA Job Openings"
                />

                <meta
                    name="og:description"
                    content="Apply for 1000 Software, Engineering, MBA Sales, Finance, Marketing, Diploma, Commerce, Arts  Job Openings on Freshment. Get Daily Job Alerts. ✓ Work from Home ✓ Online ✓ Freshers"
                />

                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://freshmentapp.com" />
                <meta
                    property="og:determiner"
                    content="Post Jobs To Get Candidates | Hire Staff Easily In 72 Hours"
                />
                <meta
                    name="twitter:title"
                    content="Apply for 1000+ Job Vacancies on Freshment, Search Fresher Jobs, Engineering, MBA Job Openings"
                />

                <meta
                    name="twitter:description"
                    content="Apply for 1000 Software, Engineering, MBA Sales, Finance, Marketing, Diploma, Commerce, Arts  Job Openings on Freshment. Get Daily Job Alerts. ✓ Work from Home ✓ Online ✓ Freshers"
                />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:creator" content="freshment" />
                <meta
                    name="twitter:image"
                    content="https://freshmentapp.com/static/media/about-office-1.1b443800.png"
                />
                <meta http-equiv="Content-Language" content="en_US" />
                <meta
                    name="keywords"
                    content="job search, fresherjobs, work, employment, career, resumes, job vacancies, government jobs, careerbuilder, part time jobs, work from home jobs, recruitment, software engineer jobs, jobs in 2023, internships, Fresher Jobs, apna jobs, job portal, job search app, free job posting, apply job online, job portals in india, job portals in gujarat, local jobs, iti jobs, data entry jobs, engineering jobs, mba jobs, mtech jobs, best job search app, internships"
                />
            </Helmet>

            <div className="min-h-screen bg-slate-50 pt-8 pb-20 font-sans">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <header className="mb-10 bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-sm relative overflow-hidden">
                        {/* Decorative Background Elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-brand-100 to-indigo-50 rounded-full blur-3xl -mr-20 -mt-20 opacity-60 pointer-events-none"></div>
                        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-orange-50 to-amber-50 rounded-full blur-2xl -ml-10 -mb-10 opacity-60 pointer-events-none"></div>

                        <div className="relative z-10">
                            <nav
                                className="flex items-center gap-2 text-sm font-medium text-slate-500 mb-6"
                                aria-label="Breadcrumb"
                            >
                                <button
                                    onClick={() => navigate("/")}
                                    className="hover:text-brand-600 transition-colors"
                                >
                                    Home
                                </button>
                                <ChevronRight className="w-4 h-4" />
                                <span className="text-brand-600 font-bold" aria-current="page">
                                    Fresher Jobs
                                </span>
                            </nav>

                            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                                <div className="max-w-3xl">
                                    <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight mb-4 leading-tight">
                                        Find Your{" "}
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-indigo-600">
                                            Freshers Jobs
                                        </span>
                                    </h1>
                                    <p className="text-lg text-slate-600 font-medium leading-relaxed mb-6">
                                        Apply for 1000 Software, Engineering, MBA Sales, Finance, Marketing, Diploma, Commerce, Arts Job Openings on Freshment. Get Daily Job Alerts.
                                    </p>
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 border border-brand-100 rounded-lg text-brand-700 font-bold text-sm">
                                        <span className="relative flex h-2.5 w-2.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-500"></span>
                                        </span>
                                        Showing {filteredItems?.totalRecords || 0} active opportunities
                                    </div>
                                </div>

                                <div className="hidden sm:flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-2 rounded-xl font-bold shadow-lg shadow-slate-900/20">
                                    {isFiltersLoaded && (
                                        <MobileFilters
                                            filters={filters}
                                            handleFilterChange={handleFilterChange}
                                            totalResults={filteredItems?.totalRecords || 0}
                                            onClearAll={clearFilters}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </header>

                    <div className="flex flex-col lg:flex-row gap-8">
                        <div className="flex gap-[32px]">
                            <div className="min-w-[300px] sm:hidden">
                                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm mb-5">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search jobs, companies..."
                                            className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                                            value={search || ""}
                                            onChange={(e) => {
                                                handleSearch(e.target.value)
                                            }}
                                        />
                                    </div>
                                </div>

                                {isFiltersLoaded && (
                                    <JobFilter
                                        filters={filters}
                                        handleFilterChange={handleFilterChange}
                                    />
                                )}
                            </div>

                            <div className="grow-1 w-full">
                                {/* Active filter tags */}
                                {hasActiveFilters && (
                                    <div className="flex flex-wrap items-center gap-2 mb-4">
                                        {/* Category / Date / Qualification filter tags */}
                                        {selectedFilters.map((f) => (
                                            <span
                                                key={f.value}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 text-xs font-bold border border-brand-100"
                                            >
                                                {f.label}
                                                <button
                                                    onClick={() => {
                                                        const updatedFilters = filters.map((group) =>
                                                            group.value === f.groupValue
                                                                ? {
                                                                    ...group,
                                                                    filters: group.filters.map((ff) =>
                                                                        ff.value === f.value
                                                                            ? { ...ff, isChecked: false }
                                                                            : ff,
                                                                    ),
                                                                }
                                                                : group,
                                                        );
                                                        setFilters(updatedFilters);
                                                        handleFilterChange(updatedFilters);
                                                    }}
                                                    className="hover:text-brand-900"><X className="w-3 h-3" /></button>
                                            </span>
                                        ))}

                                        {/* Clear all button */}
                                        <button
                                            onClick={clearFilters}
                                            className="text-xs font-bold text-slate-400 hover:text-slate-700 underline ml-1"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 xl:grid-cols-1 gap-6">
                                    {filteredItems?.data?.map((job: any) => (
                                        <GovJobs {...job} key={job.id} />
                                    ))}
                                    {filteredItems?.data?.length === 0 && (
                                        <div className="col-span-2 xl:col-span-1 flex flex-col items-center justify-center py-20 text-center bg-white rounded-3xl border border-slate-100 shadow-sm mt-4">
                                            <div className="w-24 h-24 bg-slate-50 flex items-center justify-center rounded-full mb-6">
                                                <Briefcase className="h-12 w-12 text-[#cbd5e1]" />
                                            </div>
                                            <h3 className="text-[22px] font-bold text-[#1e293b] mb-3">No jobs found</h3>
                                            <p className="text-[#64748b] font-medium text-[15px] mb-8 max-w-md mx-auto leading-relaxed">
                                                We couldn't find any jobs matching your current filters.<br />
                                                Try adjusting your search or clearing some filters.
                                            </p>
                                            <button
                                                onClick={clearFilters}
                                                className="px-6 py-3 bg-[#3b82f6] text-white font-bold rounded-xl hover:bg-[#2563eb] transition-colors"
                                            >
                                                Clear All Filters
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {filteredItems?.data?.length > 0 && <div className="mt-[34px]">
                                    <PaginationComponent
                                        totalRecords={filteredItems?.totalRecords}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        handlePageChange={handlePageChange}
                                        setPageTo={+page! === 0 ? 1 : +page!}
                                    />
                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default FreshersJob;
